import {
  FC,
  createContext,
  useReducer,
  useContext,
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  fetchMe,
  logout as logoutRequest,
  signIn as signInRequest,
  verifyOtp as verifyOtpRequest
} from 'services/api/auth';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setIsLoggedIn, setUser, setUserLoading } from 'store/user/slice';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { LANGUAGES } from 'constants/languages';
import { selectUserIsLoading } from 'store/user/selectors';

import {
  State,
  Dispatch,
  ActionTypes,
  DispatchContext,
  IAuthProps,
  PermissionsState,
  LoginForm
} from './types';
import reducer from './reducers';
import {
  parentOrgStorage,
  refreshTokenStorage,
  tokenStorage
} from 'utils/storage';
import { BlockedModal } from 'components/shared';
import { getLanguageAccess } from 'utils/getLanguageAccess';
import { Loading } from 'components/ui';

const initialState: State = {
  isBlockedVisible: false,
  tmpToken: '',
  languages: LANGUAGES
};

const AuthStateContext = createContext<State | undefined>(undefined);
const AuthDispatchContext = createContext<
  { dispatch: Dispatch; getUserData(): void; signOut(): void } | undefined
>(undefined);

const PermissionsStateContext = createContext<PermissionsState>({});

const AuthProvider: FC<IAuthProps> = ({ children }) => {
  const dispatchRedux = useAppDispatch();

  const isLoading = useAppSelector(selectUserIsLoading);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [permissionValues, setPermissionValues] = useState<
    Record<string, number>
  >({});

  const signOut = useCallback(async () => {
    // Fire-and-forget; the instance interceptor adds the Bearer header.
    logoutRequest();
    tokenStorage.remove();
    refreshTokenStorage.remove();
    parentOrgStorage.remove();
    dispatchRedux(setUserLoading(false));
    dispatchRedux(setIsLoggedIn(false));
    dispatchRedux(setUser(null));
  }, [dispatchRedux]);

  const getUserData = useCallback(async () => {
    try {
      const me = await fetchMe();

      if (me) {
        dispatchRedux(setUser(me));
        dispatch({
          type: ActionTypes.SET_LANGUAGES,
          data: getLanguageAccess(me?.role?.name ?? me?.role_name)
        });
        // /auth/me already returns permissions: Record<section, value>
        // pre-rolled — no separate GET_USER_PERMISSIONS call needed.
        setPermissionValues((me.permissions ?? {}) as Record<string, number>);
      }

      dispatchRedux(setIsLoggedIn(true));
      setTimeout(() => {
        dispatchRedux(setUserLoading(false));
      }, 100);
    } catch {
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tokenStorage.get()) {
      getUserData();
    } else {
      dispatchRedux(setUserLoading(false));
    }
  }, [dispatchRedux, getUserData]);

  const closeModal = () => {
    dispatch({
      type: ActionTypes.SET_BLOCKED_VISIBLE,
      data: false
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={{ signOut, getUserData, dispatch }}>
        <PermissionsStateContext.Provider value={permissionValues}>
          {children}
          <BlockedModal open={state.isBlockedVisible} onClose={closeModal} />
        </PermissionsStateContext.Provider>
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

const useAuthStateContext = (): State => {
  const context = useContext(AuthStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useAuthStateContext must be used within a useAuthStateContext'
    );
  }

  return context;
};

const useAuthDispatchContext = (): DispatchContext => {
  const context = useContext(AuthDispatchContext);
  const dispatchRedux = useAppDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();

  if (typeof context === 'undefined') {
    throw new Error(
      'useAuthDispatchContext must be used within a useAuthDispatchContext'
    );
  }

  const { dispatch, signOut, getUserData } = context;

  const refetchUserData = useCallback(async () => {
    try {
      const me = await fetchMe();

      if (me) {
        dispatchRedux(setUser(me));
      }
    } catch {
      //
    }
  }, [dispatchRedux]);

  const signIn = useCallback(
    async (params: LoginForm) => {
      try {
        if (!executeRecaptcha) {
          dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });

          return Promise.reject('');
        }

        const recaptchaToken = await executeRecaptcha('login');
        const data = await signInRequest({ ...params, recaptchaToken });

        if (data.auth2fa) {
          dispatch({
            type: ActionTypes.SET_TMP_TOKEN,
            data: data.tmpToken
          });
        }

        return data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        if (err.response?.status === 429) {
          dispatch({
            type: ActionTypes.SET_BLOCKED_VISIBLE,
            data: true
          });

          return Promise.reject(err);
        }

        return Promise.reject(err);
      }
    },
    [dispatch, executeRecaptcha]
  );

  const verify2FA = useCallback(
    async (otp: string, tmpToken: string) =>
      verifyOtpRequest({ otp, token: tmpToken }),
    []
  );

  return { signOut, signIn, verify2FA, getUserData, refetchUserData };
};

const usePermissionsStateContext = (): PermissionsState => {
  const context = useContext(PermissionsStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'usePermissionsStateContext must be used within a PermissionsStateContext'
    );
  }

  return context;
};

export default AuthProvider;
export {
  useAuthDispatchContext,
  useAuthStateContext,
  usePermissionsStateContext
};
